import { UITextView, UIView, YES } from "uicore-ts"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTaxDebt } from "../SocketClientFunctions"


// ============================================================================
// TaxDebtTooltipView
// Bootstrap 5.3-styled popover that floats above the badge on hover.
// Added as a subview of the root view so it is never clipped by table overflow.
// ============================================================================

export class TaxDebtTooltipView extends UIView {
    
    private static readonly _TOOLTIP_WIDTH = 320
    private static readonly _TOOLTIP_HEIGHT = 130  // header(28) + padding(8) + 4×row(22) + 3×gap(2)
    
    // ── Header ──────────────────────────────────────────────────────────────
    private readonly _headerView = new UIView()
        .addedAsSubviewToView(this)
    
    private readonly _headerLabel = new UITextView()
        .configuredWithObject({ text: "Maksuvõlg", isSingleLine: YES })
        .addedAsSubviewToView(this._headerView)
    
    // ── Rows ─────────────────────────────────────────────────────────────────
    private readonly _amountRow = this._makeRow("Summa:")
    private readonly _contestedRow = this._makeRow("Vaidlustatud:")
    private readonly _scheduleRow = this._makeRow("Graafik kuni:")
    private readonly _oldestDueRow = this._makeRow("Vanima nõude tähtaeg:")
    
    
    constructor(elementID?: string) {
        super(elementID);
        
        // Popover card
        (this as TaxDebtTooltipView).configureWithObject({
            backgroundColor: RHColor.textWhite,
            hidden: YES,
            style: {
                borderRadius: "6px",
                border: `1px solid ${RHColor.borderLight.stringValue}`,
                boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.15)",
                pointerEvents: "none",
                zIndex: "9999"
            }
        })
        
        // Header bar
        this._headerView.configureWithObject({
            backgroundColor: RHColor.secondary,
            style: {
                borderRadius: "5px 5px 0 0",
                borderBottom: `1px solid ${RHColor.borderLight.stringValue}`
            }
        })
        
        this._headerLabel.configureWithObject({
            textColor: RHColor.textWhite,
            style: {
                fontWeight: "600",
                fontSize: "0.8em"
            }
        })
    }
    
    
    private _makeRow(label: string): { labelView: UITextView; valueView: UITextView } {
        const labelView = new UITextView()
            .configuredWithObject({
                text: label,
                isSingleLine: YES,
                style: { fontSize: "0.8em", color: RHColor.textMuted.stringValue }
            })
            .addedAsSubviewToView(this)
        const valueView = new UITextView()
            .configuredWithObject({
                isSingleLine: YES,
                style: { fontSize: "0.8em", fontWeight: "500", textAlign: "right" }
            })
            .addedAsSubviewToView(this)
        return { labelView, valueView }
    }
    
    
    setContent(taxDebt: RHTaxDebt, headerColor: RHColor) {
        this._headerView.backgroundColor = headerColor
        
        const hasSchedule = parseFloat(taxDebt.onPaymentSchedule.replace(",", ".")) > 0
        
        this._amountRow.valueView.configureWithObject({
            text: RHEuroFormatter.formattedAmountText(taxDebt.amount),
            style: { color: RHColor.danger.stringValue }
        })
        
        this._contestedRow.valueView.configureWithObject({
            text: RHEuroFormatter.formattedAmountText(taxDebt.contested),
            style: { color: RHColor.textMuted.stringValue }
        })
        
        this._scheduleRow.valueView.configureWithObject({
            text: hasSchedule ? taxDebt.paymentScheduleEndDate : "—",
            style: { color: hasSchedule ? RHColor.success.stringValue : RHColor.textMuted.stringValue }
        })
        
        this._oldestDueRow.valueView.configureWithObject({
            text: taxDebt.oldestUnpaidDueDate,
            style: { color: RHColor.warning.stringValue }
        })
        
        this.setNeedsLayout()
    }
    
    
    /**
     * Position the tooltip so it floats above `badgeView` in root-view coordinate space.
     * Uses getBoundingClientRect on the native elements combined with UIView.pageScale to
     * convert from page pixels to logical framework coordinates.
     * Call this each time the tooltip is shown.
     */
    positionedAboveBadgeView(badgeView: UIView) {
        const containerView = this.superview!
        const scale = UIView.pageScale
        const containerRect = containerView.viewHTMLElement.getBoundingClientRect()
        const badgeRect = badgeView.viewHTMLElement.getBoundingClientRect()
        
        const badgeX = (badgeRect.left - containerRect.left) / scale
        const badgeY = (badgeRect.top - containerRect.top) / scale
        const badgeW = badgeRect.width / scale
        
        const w = TaxDebtTooltipView._TOOLTIP_WIDTH
        const h = TaxDebtTooltipView._TOOLTIP_HEIGHT
        const gap = 6
        
        const centredX = badgeX + badgeW / 2 - w / 2
        const clampedX = Math.max(4, Math.min(centredX, containerView.bounds.width - w - 4))
        const y = badgeY - h - gap
        
        this.frame = this.frame
            .rectangleWithX(clampedX)
            .rectangleWithY(y)
            .rectangleWithWidth(w)
            .rectangleWithHeight(h)
        
        return this
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = 8
        const headerHeight = 28
        const rowHeight = 22
        const rowGap = 2
        
        const headerFrame = this.bounds.rectangleWithHeight(headerHeight).rectangleWithInsets(0, 1, 0, 0)
        this._headerView.frame = headerFrame
        this._headerLabel.frame = this._headerView.bounds.rectangleWithInset(padding)
        
        const rows = [this._amountRow, this._contestedRow, this._scheduleRow, this._oldestDueRow]
        let rowFrame = headerFrame.rectangleWithInsets(padding, padding, 0, 0).rectangleForNextRow(rowGap, rowHeight)
        
        for (const row of rows) {
            rowFrame.distributeViewsAlongWidth([row.labelView, row.valueView], [0.55, 0.45])
            rowFrame = rowFrame.rectangleForNextRow(rowGap, rowHeight)
        }
    }
    
}
